/**
 * @file PortalActionsList.js
 * @description This page displays a list of portal actions and their respective details.
 */

import styles from './PortalActions.module.scss';
import { useState, useEffect } from 'react';
import { getContentType } from '../helpers/getContentType';
import Spinner from '../ui/spinners/Spinner';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import PaginationComponent from '../helpers/PaginationComponent';
import { FilterModalV2 } from '../helpers/FilterComponent_V2';
import { formatDateToMMDDYYYY } from '../helpers/DateUtils';
import { getAllPortalActions } from '../../services/containerService';
import { useNavigate } from 'react-router-dom';
import { getText } from '../../i18n';

const SortDirections = {
    ASCENDING: 'asc',
    DESCENDING: 'desc',
};

const PortalActionsList = () => {
    const [actions, setActions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [sortConfig, setSortConfig] = useState({
        key: 'Title',
        direction: SortDirections.ASCENDING,
    });
    const [activeModal, setActiveModal] = useState(null);
    const [selectedOptions, setSelectedOptions] = useState({});
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const pageSize = 10;

    const navigate = useNavigate();

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedOptions, sortConfig]);

    useEffect(() => {
        loadPortalActions();
    }, [currentPage, sortConfig, selectedOptions]);

    const loadPortalActions = async () => {
        setIsLoading(true);
        try {
            const fetchedActions = await getAllPortalActions(
                pageSize,
                currentPage,
                sortConfig.key,
                sortConfig.direction,
                selectedOptions,
            );

            setActions(fetchedActions.actions);
            setTotalItems(fetchedActions.totalActions);

            const calculatedTotalPages = Math.ceil(
                fetchedActions.totalActions / pageSize,
            );
            setTotalPages(calculatedTotalPages);
        } catch (error) {
            console.error('Failed to fetch actions', error);
        } finally {
            setIsLoading(false);
        }
    };

    const openModal = column => setActiveModal(column);
    const closeModal = () => setActiveModal(null);

    const handleApply = (column, values) => {
        setSelectedOptions(prev => ({
            ...prev,
            [column]: values,
        }));
        setCurrentPage(1); // Reset to first page when filters change
    };

    const clearFilters = () => {
        setSelectedOptions({});
        setCurrentPage(1);
    };

    const handleSortByColumn = column => {
        setSortConfig(prevConfig => {
            const isCurrentColumn = prevConfig.key === column;
            const newDirection =
                isCurrentColumn &&
                prevConfig.direction === SortDirections.ASCENDING
                    ? SortDirections.DESCENDING
                    : SortDirections.ASCENDING;

            return {
                key: column,
                direction: newDirection,
            };
        });
        setCurrentPage(1);
    };

    const handlePageChange = page => {
        setCurrentPage(page);
    };

    const calculatePercentage = (part, total) => {
        return total === 0 ? 0 : Math.round((part / total) * 1000) / 10;
    };

    const renderSortButton = column => {
        const isActive =
            sortConfig.key === column &&
            sortConfig.direction === SortDirections.ASCENDING;
        return (
            <span
                onClick={() => handleSortByColumn(column)}
                className={`px-2 ${styles.cursorPointer}`}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        handleSortByColumn(column);
                    }
                }}
            >
                <svg width={11} height={6} viewBox="0 0 11 6" fill="none">
                    <path
                        transform={isActive ? 'rotate(180 5.25 3)' : ''}
                        d="M1 0.75L5.25 5.25L9.5 0.75"
                        stroke="#2F2B43"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </span>
        );
    };

    const renderFilterModal = (column, options, transformOptions) => {
        const isSelected =
            selectedOptions[column] && selectedOptions[column].length > 0;
        return (
            <>
                <span
                    onClick={() => openModal(column)}
                    className={styles.cursorPointer}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            openModal(column);
                        }
                    }}
                >
                    {isSelected ? (
                        <svg
                            style={{
                                marginTop: '4px',
                                display: 'inline-block',
                                width: '1em',
                                height: '1em',
                            }}
                        >
                            <path
                                d="M1.08871 2.81875C0.701376 2.38585 0.507709 2.1694 0.500404 1.98544C0.494058 1.82564 0.562731 1.67205 0.686053 1.57022C0.828016 1.453 1.11846 1.453 1.69935 1.453H9.30065C9.88154 1.453 10.172 1.453 10.3139 1.57022C10.4373 1.67205 10.5059 1.82564 10.4996 1.98544C10.4923 2.1694 10.2986 2.38585 9.91129 2.81875L6.98903 6.0848C6.91182 6.17109 6.87322 6.21424 6.84569 6.26335C6.82128 6.3069 6.80336 6.35378 6.79252 6.40252C6.78029 6.45747 6.78029 6.51537 6.78029 6.63116V9.36948C6.78029 9.46963 6.78029 9.5197 6.76414 9.563C6.74986 9.60125 6.72665 9.63554 6.69643 9.663C6.66223 9.69408 6.61574 9.71268 6.52275 9.74987L4.78156 10.4463C4.59333 10.5216 4.49922 10.5593 4.42367 10.5436C4.35761 10.5299 4.29963 10.4906 4.26235 10.4344C4.21971 10.37 4.21971 10.2687 4.21971 10.066V6.63116C4.21971 6.51537 4.21971 6.45747 4.20748 6.40252C4.19664 6.35378 4.17872 6.3069 4.15431 6.26335C4.12678 6.21424 4.08818 6.17109 4.01096 6.0848L1.08871 2.81875Z"
                                fill="#1366FF"
                                stroke="#1366FF"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    ) : (
                        <i
                            className="bi bi-funnel"
                            style={{ fontSize: '0.7em' }}
                            aria-label={`Filter ${column}`}
                        ></i>
                    )}
                </span>
                {activeModal === column && (
                    <FilterModalV2
                        column={column}
                        options={Array.from(new Set(options))}
                        selectedOptions={selectedOptions[column] || []}
                        onApply={handleApply}
                        onClose={closeModal}
                        transformOptions={transformOptions}
                    />
                )}
            </>
        );
    };

    return isLoading ? (
        <Spinner width={50} className={styles.loadingSpinner} />
    ) : (
        <div
            className="page-content"
            style={{ height: '100%', overflowY: 'auto' }}
        >
            <div className="card" style={{ minHeight: '100%' }}>
                <div
                    className="card-body px-0 py-4 d-flex flex-column"
                    style={{ minHeight: '100%' }}
                >
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div
                            className="float-start d-flex"
                            style={{ flexDirection: 'column', display: 'flex' }}
                        >
                            <p className="page-title card-title ms-4 me-5">
                                {getText('msg_c3cd636a585b')}
                            </p>
                            <p
                                className="ing-portals-subtitle mt-1"
                                style={{ marginTop: '10px' }}
                            >
                                {getText('msg_924dda6eaf15')}
                            </p>
                        </div>
                        <div className="float-end d-flex align-items-center">
                            <span
                                type="button"
                                onClick={clearFilters}
                                className="ing-text-buttons me-4"
                            >
                                <i className="bi bi-arrow-counterclockwise me-2" />
                                {getText('msg_381c4bf1d409')}
                            </span>
                        </div>
                    </div>
                    <div className="w-100">
                        <div className={styles.tableContainer}>
                            <table
                                className={`table table-hover align-middle ${styles.tableDiv}`}
                            >
                                <thead className="custom-table-header table-light">
                                    <tr>
                                        <th className={styles.tableHeader}>
                                            <span className="table-column-header-text">
                                                {getText('msg_768e0c1c6957')}
                                            </span>
                                            {renderSortButton('Title')}
                                            {renderFilterModal(
                                                'Title',
                                                actions.map(
                                                    item => item.containerTitle,
                                                ),
                                            )}
                                        </th>
                                        <th className={styles.tableHeader}>
                                            <span className="table-column-header-text">
                                                {getText('msg_b55223c7bdc7')}
                                            </span>
                                            {renderSortButton('Portal')}
                                            {renderFilterModal(
                                                'Portal',
                                                actions.map(
                                                    item => item.portal.name,
                                                ),
                                            )}
                                        </th>
                                        <th className={styles.tableHeader}>
                                            <span className="table-column-header-text">
                                                {getText('msg_863ed7dbce40')}
                                            </span>
                                            {renderSortButton('Container Type')}
                                            {renderFilterModal(
                                                'containerTypeIds',
                                                actions.map(
                                                    item =>
                                                        item.containerTypeId,
                                                ),
                                                getContentType,
                                            )}
                                        </th>
                                        <th
                                            className={`${styles.tableHeader} ${styles.alignCenter}`}
                                        >
                                            <span className="table-column-header-text">
                                                {getText('msg_63051d96a2df')}
                                            </span>
                                            {renderSortButton(
                                                'Acknowledgment Type',
                                            )}
                                            {renderFilterModal(
                                                'containerAcknowledgmentTypeIds',
                                                [1, 0],
                                                value =>
                                                    value === 1 ? 'Yes' : 'No',
                                            )}
                                        </th>
                                        <th
                                            className={`${styles.tableHeader} ${styles.alignCenter}`}
                                        >
                                            <span className="table-column-header-text">
                                                {getText('msg_a1b308ec704a')}
                                            </span>
                                            {renderSortButton('Due Date')}
                                            {renderFilterModal(
                                                'containerDueDates',
                                                actions.map(
                                                    item => item.dueDate,
                                                ),
                                            )}
                                        </th>
                                        <th className={styles.tableHeader}>
                                            <span className="table-column-header-text">
                                                {getText('msg_be763e9aba2a')}
                                            </span>
                                            {renderSortButton('Completion')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {actions.length > 0
                                        ? actions.map(action => (
                                              <tr key={action.containerId}>
                                                  <td
                                                      className={
                                                          styles.titleCell
                                                      }
                                                      onClick={() =>
                                                          navigate(
                                                              `/actions/view/${action.containerId}`,
                                                          )
                                                      }
                                                      role="button"
                                                      tabIndex={0}
                                                      onKeyDown={e => {
                                                          if (
                                                              e.key ===
                                                                  'Enter' ||
                                                              e.key === ' '
                                                          ) {
                                                              navigate(
                                                                  `/actions/view/${action.containerId}`,
                                                              );
                                                          }
                                                      }}
                                                  >
                                                      {action.containerTitle ||
                                                          'Untitled Container'}
                                                  </td>
                                                  <td
                                                      className={
                                                          styles.subtitleCell
                                                      }
                                                  >
                                                      {action.portal.name}
                                                  </td>
                                                  <td
                                                      className={
                                                          styles.subtitleCell
                                                      }
                                                  >
                                                      {getContentType(
                                                          action.containerTypeId,
                                                      )}
                                                  </td>
                                                  <td
                                                      className={`${styles.subtitleCell} ${styles.alignCenter}`}
                                                  >
                                                      {action.acknowledgmentTypeId ===
                                                      1
                                                          ? 'Y'
                                                          : 'N'}
                                                  </td>
                                                  <td
                                                      className={`${styles.subtitleCell} ${styles.alignCenter}`}
                                                  >
                                                      {formatDateToMMDDYYYY(
                                                          action.dueDate,
                                                      )}
                                                  </td>
                                                  <td
                                                      className={
                                                          styles.subtitleCell
                                                      }
                                                  >
                                                      {`${action.stats.completedIndividuals}/${action.stats.totalIndividuals} (${calculatePercentage(action.stats.completedIndividuals, action.stats.totalIndividuals)}%)`}
                                                  </td>
                                              </tr>
                                          ))
                                        : null}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    {actions.length === 0 && !isLoading && (
                        <NotFoundBox
                            backgroundColor="#f0f4ff"
                            title={getText('msg_aca14ec90fb5')}
                            description={getText('msg_5328029ad816')}
                        />
                    )}
                    {/* {totalPages > 1 && ( */}
                    <div className="d-flex justify-content-center mt-4">
                        <PaginationComponent
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    </div>
                    {/* )} */}
                </div>
            </div>
        </div>
    );
};

export default PortalActionsList;
